<template>
  <div class="mb-4 p-3 border rounded">
    <h2>Сгенерировать список рецептов</h2>

    <!-- Form for user input -->
    <form @submit.prevent="generateList" class="form">
      <!-- Prompt input -->
      <div class="mt-3 mb-3">
        <label for="listPrompt" class="form-label"
          >Тема или ключевые слова для списка:</label
        >
        <input
          type="text"
          id="listPrompt"
          v-model="prompt"
          class="form-control"
          placeholder="например, '10 названий блюд из рыбы' или '5 рецептов запеканок'"
        />
      </div>
      <button
        type="submit"
        class="btn btn-outline-success"
        :disabled="loadingList"
      >
        <span
          v-if="loadingList"
          class="spinner-border spinner-border-sm"
          role="status"
          aria-hidden="true"
        ></span>
        {{ loadingList ? "Генерация..." : "Сгенерировать список" }}
      </button>
    </form>

    <!-- Error messages -->
    <div v-if="errorList" class="mt-3 text-danger">{{ errorList }}</div>

    <!-- Generated list -->
    <div v-if="generatedList.length > 0" class="mt-4">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h3>Предложенные рецепты:</h3>
        <button
          @click="toggleSelectAll"
          class="btn btn-sm btn-outline-secondary"
        >
          {{ allSelected ? "Снять все" : "Отметить все" }}
        </button>
      </div>

      <!-- List of generated recipes names -->
      <ul class="list-group">
        <li
          v-for="(recipe, index) in generatedList"
          :key="index"
          class="list-group-item d-flex justify-content-between align-items-center"
        >
          <div>
            <input
              class="form-check-input me-2"
              type="checkbox"
              :value="recipe.name"
              :id="'recipeCheck' + index"
              v-model="recipe.selected"
            />
            <label :for="'recipeCheck' + index">{{ recipe.name }}</label>
          </div>
        </li>
      </ul>

      <!-- Button to process selected recipes -->
      <button
        v-if="generatedList.some((r) => r.selected)"
        @click="generateSelectedRecipes"
        class="btn btn-outline-success mt-3"
        :disabled="loadingGeneratedRecipes"
      >
        <span
          v-if="loadingGeneratedRecipes"
          class="spinner-border spinner-border-sm"
          role="status"
          aria-hidden="true"
        ></span>
        {{
          loadingGeneratedRecipes
            ? "Генерация выбранных..."
            : "Сгенерировать выбранные рецепты"
        }}
      </button>
    </div>
    <!-- Placeholder for displaying generated recipes or their status -->
    <div v-if="Object.keys(generatedRecipesResults).length > 0" class="mt-4">
      <h4>Результаты генерации выбранных рецептов:</h4>
      <div v-if="canAddAnyRecipe" class="mb-3">
        <button
          @click="addAllGeneratedRecipes"
          class="btn btn-info"
          :disabled="
            addingAllRecipesInProgress ||
            Object.values(addingRecipeStatus).some((s) => s)
          "
        >
          <span
            v-if="addingAllRecipesInProgress"
            class="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
          ></span>
          {{
            addingAllRecipesInProgress
              ? "Добавление всех..."
              : "Добавить все в базу"
          }}
        </button>
      </div>

      <div
        v-for="(result, dishName) in generatedRecipesResults"
        :key="dishName"
        class="mb-3 p-3 border rounded"
      >
        <h5>{{ dishName }}</h5>
        <div v-if="!isErrorResult(result)" class="mt-2">
          <div class="d-flex justify-content-end mb-2">
            <button
              @click="toggleRecipeDisplayMode(dishName)"
              class="btn btn-sm btn-outline-secondary"
            >
              <i
                :class="
                  recipeDisplayMode[dishName] === 'json'
                    ? 'bi bi-eye-fill'
                    : 'bi bi-filetype-json'
                "
              ></i>
              {{
                recipeDisplayMode[dishName] === "json"
                  ? " Показать форматированный"
                  : " Показать JSON"
              }}
            </button>
          </div>

          <div v-if="recipeDisplayMode[dishName] === 'json'">
            <pre><code class="language-json">{{ formatJson(result) }}</code></pre>
          </div>

          <div v-else>
            <!-- Display formatted recipe using RecipeView -->
            <RecipeView
              :recipe="parseRecipeJson(result)"
              :short="false"
              class="mb-2"
            />
          </div>

          <div class="mt-2">
            <button
              @click="addGeneratedRecipe(dishName, result)"
              class="btn btn-sm btn-primary"
              :disabled="
                isRecipeAdded(dishName) ||
                addingRecipeStatus[dishName] ||
                addingAllRecipesInProgress
              "
            >
              <span
                v-if="addingRecipeStatus[dishName]"
                class="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
              {{
                addingRecipeStatus[dishName]
                  ? "Добавление..."
                  : isRecipeAdded(dishName)
                  ? "Добавлено"
                  : "Добавить в базу"
              }}
            </button>
          </div>
        </div>
        <div v-else class="text-warning">{{ result }}</div>
      </div>
    </div>
  </div>
</template>

<script>
// Command to generate a list of recipes
const CMD_RECIPE_GENERATE_LIST = "recipe.generate_list";
const CMD_RECIPE_GENERATE_SELECTED = "recipe.generate_selected";
const CMD_RECIPE_ADD = "recipe.add/";

import RecipeView from "./RecipeView.vue";

export default {
  name: "RecipeGenerateList",
  components: {
    RecipeView,
  },

  data() {
    return {
      /**
       * The user input for the list topic.
       * @type {string}
       */
      prompt: "",

      /**
       * The list of generated recipes with their names and selection state.
       * @type {Array<{ name: string, selected: boolean }>}
       */
      generatedList: [],

      /**
       * Whether the component is currently loading data for the initial list.
       * @type {boolean}
       */
      loadingList: false,

      /**
       * The results from generating selected recipes.
       * Stores JSON strings or error messages under the dish name keys.
       * @type {Object<string, string>}
       */
      generatedRecipesResults: {},

      /**
       * Whether the component is currently loading data for generating selected recipes.
       * @type {boolean}
       */
      loadingGeneratedRecipes: false,

      /**
       * Tracks which recipes have been successfully added to the database.
       * Stores true values under the dish name keys.
       * @type {Object<string, boolean>}
       */
      addedRecipes: {},

      /**
       * Tracks loading state for individual add operations.
       * Stores true values under the dish name keys.
       * @type {Object<string, boolean>}
       */
      addingRecipeStatus: {},

      /**
       * Whether the "Add All" operation is currently in progress.
       * @type {boolean}
       */
      addingAllRecipesInProgress: false,

      /**
       * To store error messages.
       * @type {string}
       */
      errorList: "",

      /**
       * Stores display mode for each recipe: 'view' or 'json'.
       * @type {Object<string, string>}
       */
      recipeDisplayMode: {},
    };
  },

  methods: {
    generateList() {
      if (!this.prompt.trim()) {
        this.errorList = "Пожалуйста, введите тему для списка рецептов.";
        return;
      }
      this.loadingList = true;
      this.errorList = "";
      this.generatedList = [];
      this.generatedRecipesResults = {};
      this.addedRecipes = {};
      this.addingRecipeStatus = {};
      this.recipeDisplayMode = {};
      this.addingAllRecipesInProgress = false;
      this.teoweb.sendCmd(CMD_RECIPE_GENERATE_LIST, this.prompt);
    },

    toggleSelectAll() {
      const targetState = !this.allSelected;
      this.generatedList.forEach((recipe) => (recipe.selected = targetState));
    },

    generateSelectedRecipes() {
      const selectedNames = this.generatedList
        .filter((r) => r.selected)
        .map((r) => r.name);

      if (selectedNames.length > 0) {
        this.loadingGeneratedRecipes = true;
        this.errorList = "";
        this.generatedRecipesResults = {};
        this.addedRecipes = {};
        this.recipeDisplayMode = {};
        this.addingRecipeStatus = {};
        this.addingAllRecipesInProgress = false;
        this.teoweb.sendCmd(
          CMD_RECIPE_GENERATE_SELECTED,
          JSON.stringify(selectedNames)
        );
      } else {
        this.errorList = "Пожалуйста, выберите хотя бы один рецепт из списка.";
      }
    },

    isErrorResult(resultString) {
      // Consider a result an "error" or "non-JSON informational message"
      // if it's a string and starts with "Сгенерировать не удалось:" or "Error:" (for now)
      return (
        typeof resultString === "string" &&
        (resultString.startsWith("Сгенерировать не удалось:") ||
          resultString.startsWith("Error:"))
      );
    },

    formatJson(jsonString) {
      try {
        return JSON.stringify(JSON.parse(jsonString), null, 2);
      } catch (e) {
        return jsonString;
      }
    },

    parseRecipeJson(jsonString) {
      try {
        return JSON.parse(jsonString);
      } catch (e) {
        console.error(
          "RecipeGenerateList: Ошибка парсинга JSON для RecipeView:",
          e,
          jsonString
        );
        return null;
      }
    },

    addGeneratedRecipe(dishName, recipeJson) {
      if (!recipeJson || this.isErrorResult(recipeJson)) {
        this.errorList = `Невозможно добавить "${dishName}", так как данные рецепта не являются корректным JSON.`;
        return;
      }
      this.addingRecipeStatus[dishName] = true;
      this.errorList = "";
      this.teoweb.sendCmd(CMD_RECIPE_ADD + dishName, recipeJson);
    },

    isRecipeAdded(dishName) {
      return !!this.addedRecipes[dishName];
    },

    resetRecipeStatus(dishName) {
      // May not be needed but good to have
      delete this.addedRecipes[dishName];
      delete this.addingRecipeStatus[dishName];
    },

    addAllGeneratedRecipes() {
      this.addingAllRecipesInProgress = true;
      let recipesQueued = 0;
      for (const dishName in this.generatedRecipesResults) {
        const result = this.generatedRecipesResults[dishName];
        if (
          !this.isErrorResult(result) &&
          !this.isRecipeAdded(dishName) &&
          !this.addingRecipeStatus[dishName]
        ) {
          this.addGeneratedRecipe(dishName, result); // This will set addingRecipeStatus[dishName] = true
          recipesQueued++;
        }
      }
      if (recipesQueued === 0) {
        this.addingAllRecipesInProgress = false; // No new recipes were queued to be added
      }
      // The addingAllRecipesInProgress flag will be more robustly set to false
      // in the reader when all individual operations complete or an error occurs.
    },

    toggleRecipeDisplayMode(dishName) {
      if (this.recipeDisplayMode[dishName] === "json") {
        this.recipeDisplayMode[dishName] = "view";
      } else {
        this.recipeDisplayMode[dishName] = "json";
      }
    },
  },

  watch: {
    generatedRecipesResults: {
      handler(newResults) {
        this.addedRecipes = {};
        // Initialize display mode for new results, default to 'view'
        for (const dishName in newResults) {
          if (!this.recipeDisplayMode[dishName]) {
            this.recipeDisplayMode[dishName] = "view";
          }
        }
        this.addingRecipeStatus = {};
        this.addingAllRecipesInProgress = false; // Reset this too
      },
      deep: true,
    },
  },

  computed: {
    allSelected() {
      return (
        this.generatedList.length > 0 &&
        this.generatedList.every((recipe) => recipe.selected)
      );
    },
    canAddAnyRecipe() {
      if (Object.keys(this.generatedRecipesResults).length === 0) {
        return false;
      }
      return Object.entries(this.generatedRecipesResults).some(
        ([dishName, result]) =>
          !this.isErrorResult(result) && !this.isRecipeAdded(dishName)
      );
    },
  },

  mounted() {
    let that = this;
    this.reader = this.teoweb.addReader(function (gw, data) {
      let dishNameForAdd;
      if (gw.err) {
        if (that.loadingList) {
          if (gw.err !== "access-denied")
            that.errorList = "Ошибка от сервера (gw.err): " + gw.err;
          that.loadingList = false;
        } else if (that.loadingGeneratedRecipes) {
          if (gw.err !== "access-denied")
            that.errorList = "Ошибка генерации выбранных (gw.err): " + gw.err;
          that.loadingGeneratedRecipes = false;
        } else {
          const activeAddOp = Object.keys(that.addingRecipeStatus).find(
            (key) => that.addingRecipeStatus[key]
          );
          if (activeAddOp) {
            that.errorList = `Ошибка добавления рецепта "${activeAddOp}": ${gw.err}`;
            that.addingRecipeStatus[activeAddOp] = false;
          }
        }
        // If "Add All" was in progress and an error occurred for one of them,
        // or if all individual adds (even if some failed) are done.
        if (
          that.addingAllRecipesInProgress &&
          !Object.values(that.addingRecipeStatus).some((status) => status)
        ) {
          that.addingAllRecipesInProgress = false;
        }
        return;
      }

      if (that.loadingList && gw.command === CMD_RECIPE_GENERATE_LIST) {
        try {
          const recipeNames = JSON.parse(data);
          if (Array.isArray(recipeNames)) {
            that.generatedList = recipeNames.map((name) => ({
              name: String(name),
              selected: false,
            }));
          } else {
            throw new Error("Ответ не является массивом.");
          }
          that.errorList = "";
        } catch (e) {
          that.errorList =
            "Ошибка обработки ответа от сервера: " +
            e.message +
            ". Получено: " +
            data;
          that.generatedList = [];
        } finally {
          that.loadingList = false;
        }
      } else if (
        that.loadingGeneratedRecipes &&
        gw.command === CMD_RECIPE_GENERATE_SELECTED
      ) {
        try {
          const results = JSON.parse(data);
          that.generatedRecipesResults = results;
          // Initialize display modes for the newly generated recipes
          for (const dishName in results) {
            // Default to 'view' mode if not already set (e.g., from a previous generation in the same session)
            if (!that.recipeDisplayMode[dishName])
              that.recipeDisplayMode[dishName] = "view";
          }
        } catch (e) {
          that.errorList =
            "Ошибка обработки ответа генерации выбранных: " +
            e.message +
            ". Получено: " +
            data;
          that.generatedRecipesResults = {};
        } finally {
          that.loadingGeneratedRecipes = false;
        }
      } else {
        const addCmdParts = gw.command.split(CMD_RECIPE_ADD);
        if (addCmdParts.length > 1 && addCmdParts[0] === "" && addCmdParts[1]) {
          dishNameForAdd = addCmdParts[1];
          if (that.addingRecipeStatus[dishNameForAdd]) {
            try {
              console.log(
                `Рецепт "${dishNameForAdd}" успешно добавлен. Ответ сервера:`,
                data
              );
              that.addedRecipes[dishNameForAdd] = true;
            } catch (e) {
              that.errorList = `Ошибка обработки ответа на добавление "${dishNameForAdd}": ${e.message}`;
            } finally {
              that.addingRecipeStatus[dishNameForAdd] = false;
              // If "Add All" was in progress, check if all individual adds are done
              if (
                that.addingAllRecipesInProgress &&
                !Object.values(that.addingRecipeStatus).some((status) => status)
              ) {
                that.addingAllRecipesInProgress = false;
              }
            }
          }
        }
      }
    });
  },

  unmounted() {
    if (this.reader) {
      this.teoweb.delReader(this.reader);
      this.reader = null;
    }
  },
};
</script>

<style scoped>
/* Styles for RecipeGenerateList component */
</style>
